<template>
    <div class="pb2-bar" @contextmenu.prevent="openPageMenu">
        <div class="pb2-bar__row">
            <span class="pb2-bar__zone pb2-bar__zone--left">
                <a
                    v-if="backUrl"
                    v-tooltip="autosave && draftChanged ? t('leave_hint') : ''"
                    :href="backUrl"
                    class="pb2-bar__back"
                >
                    <Icon icon="phosphor/regular/arrow-left" class="size-3" />
                    {{ t('back') }}
                </a>
                <button
                    v-if="title"
                    type="button"
                    class="pb2-bar__title"
                    :title="title"
                    @click="panels?.open('page')"
                >
                    {{ title }}
                </button>
                <span
                    class="pb2-bar__status"
                    :class="barStatus ? `pb2-bar__status--${barStatus.tone}` : ''"
                    role="status"
                    aria-live="polite"
                >{{ barStatus?.text ?? '' }}</span>
            </span>

            <span v-if="!isDocument" class="pb2-bar__zone pb2-bar__breakpoints" role="group">
                <button
                    v-for="option in breakpoints"
                    :key="option.key"
                    v-tooltip="option.width === null ? option.label : `${option.label} (${option.width}px)`"
                    type="button"
                    class="pb2-bar__breakpoint"
                    :class="{ 'pb2-bar__breakpoint--active': store.breakpoint.value === option.key }"
                    :aria-pressed="store.breakpoint.value === option.key"
                    :aria-label="option.label"
                    @click="store.breakpoint.value = option.key"
                >
                    <Icon :icon="option.icon" class="size-3.5" />
                </button>
            </span>

            <span class="pb2-bar__zone pb2-bar__zone--right">
                <SurfaceToggle />

                <button
                    v-tooltip="withShortcut(t('undo'), `${mod} Z`)"
                    type="button"
                    class="pb2-bar__icon"
                    :disabled="!store.canUndo.value"
                    :aria-label="t('undo')"
                    @click="store.undo()"
                >
                    <Icon icon="phosphor/regular/arrow-counter-clockwise" class="size-3.5" />
                </button>
                <button
                    v-tooltip="withShortcut(t('redo'), `${shiftMod} Z`)"
                    type="button"
                    class="pb2-bar__icon"
                    :disabled="!store.canRedo.value"
                    :aria-label="t('redo')"
                    @click="store.redo()"
                >
                    <Icon icon="phosphor/regular/arrow-clockwise" class="size-3.5" />
                </button>

                <span class="pb2-bar__sep"></span>

                <button
                    v-if="!isDocument"
                    v-tooltip="t('reload_preview')"
                    type="button"
                    class="pb2-bar__icon"
                    :aria-label="t('reload_preview')"
                    @click="chrome?.reload()"
                >
                    <Icon icon="phosphor/regular/arrows-clockwise" class="size-3.5" />
                </button>
                <!-- lite harness is an empty document — nothing to open standalone -->
                <a
                    v-if="autosave"
                    v-tooltip="t('open_in_new_window')"
                    :href="previewUrl"
                    target="_blank"
                    rel="noopener"
                    class="pb2-bar__icon"
                    :aria-label="t('open_in_new_window')"
                >
                    <Icon icon="phosphor/regular/arrow-square-out" class="size-3.5" />
                </a>

                <template v-if="autosave">
                    <span class="pb2-bar__publish">
                        <button
                            v-tooltip="publishState === 'noop' ? t('publish_no_changes') : ''"
                            type="button"
                            class="pb2-bar__publish-main"
                            :class="{
                                'pb2-bar__publish-main--success': publishState === 'success',
                                'pb2-bar__publish-main--muted': publishState === 'noop',
                            }"
                            :aria-disabled="publishState === 'noop'"
                            :disabled="store.mode.value === 'readonly' || autosave.publishing.value"
                            @click="publish"
                        >
                            <span v-if="autosave.publishing.value" class="pb2-bar__publish-spinner" aria-hidden="true"></span>
                            <Icon v-else-if="autosave.published.value" icon="phosphor/regular/check" class="size-3" />
                            {{ publishLabel }}
                        </button>
                        <button
                            type="button"
                            class="pb2-bar__publish-caret"
                            :class="{ 'pb2-bar__publish-caret--muted': publishState === 'noop' }"
                            :aria-label="t('more_actions')"
                            :aria-expanded="publishMenuOpen"
                            :disabled="store.mode.value === 'readonly' || autosave.publishing.value"
                            @click.stop="publishMenuOpen = !publishMenuOpen"
                        >
                            <Icon icon="phosphor/regular/caret-down" class="size-3" />
                        </button>

                        <span v-if="publishMenuOpen" class="pb2-bar__publish-menu">
                            <button
                                v-tooltip="draftChanged ? t('discard_tooltip') : t('discard_no_changes')"
                                type="button"
                                class="pb2-bar__publish-item"
                                :class="{ 'pb2-bar__publish-item--muted': !draftChanged }"
                                :aria-disabled="!draftChanged"
                                @click="discardFromMenu"
                            >
                                {{ t('discard') }}
                            </button>
                        </span>
                    </span>
                </template>
            </span>
        </div>

        <div v-if="draftNotice" class="pb2-bar__notice pb2-bar__notice--info">
            <span>{{ t('draft_explained') }}</span>
            <button type="button" class="pb2-bar__notice-action" @click="draftHint.dismiss()">
                {{ t('dismiss') }}
            </button>
        </div>

        <div v-if="autosave && store.mode.value === 'readonly'" class="pb2-bar__notice">
            <span>
                {{ t('locked_by') }}
                <strong>{{ autosave.lockUser.value ?? '—' }}</strong>
            </span>
            <button type="button" class="pb2-bar__notice-action" @click="takeover">
                {{ t('takeover') }}
            </button>
        </div>

        <div v-if="autosave && autosave.conflict.value" class="pb2-bar__notice">
            <span>{{ t('publish_conflict') }}</span>
            <button type="button" class="pb2-bar__notice-action" @click="autosave.publish(true)">
                {{ t('publish_force') }}
            </button>
            <button type="button" class="pb2-bar__notice-action" @click="autosave.conflict.value = false">
                {{ t('cancel') }}
            </button>
        </div>

        <div v-if="autosave && autosave.publishError.value" class="pb2-bar__notice pb2-bar__notice--danger">
            <span>{{ t(`publish_error_${autosave.publishError.value}`) }}</span>
            <button type="button" class="pb2-bar__notice-action" @click="autosave.publishError.value = null">
                {{ t('dismiss') }}
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { barSaveStatus } from '@src/Common/Builder/barStatus';
import type { BarStatusTone } from '@src/Common/Builder/barStatus';
import { useDiscardConfirmOptional } from '@src/Common/Builder/discardConfirm';
import { useBuilderHost, useBuilderT } from '@src/Common/Builder/host';
import { mod, shiftMod, withShortcut } from '@src/Common/Builder/shortcutKeys';
import SurfaceToggle from '@src/Common/Builder/Document/SurfaceToggle.vue';
import { useSurfaceControllerOptional } from '@src/Common/Builder/Document/surfaceMode';
import { useBlockContextMenuOptional } from '@src/Common/Builder/useBlockContextMenu';
import { useBuilderStore } from '@src/Common/Builder/useBuilderStore';
import { breakpointOptions, useCanvasChromeOptional } from '@src/Common/Builder/useCanvasChrome';
import { discardDraft, injectDraftAutosaveOptional, publishDraft } from '@src/Common/Builder/useDraftAutosave';
import { useOneTimeHint } from '@src/Common/Builder/useOneTimeHint';
import { usePanelManagerOptional } from '@src/Common/Builder/usePanelManager';

defineProps<{ title?: string; backUrl?: string }>();

const t = useBuilderT();
const host = useBuilderHost();
const store = useBuilderStore();
const autosave = injectDraftAutosaveOptional();
const panels = usePanelManagerOptional();
const surface = useSurfaceControllerOptional();
const chrome = useCanvasChromeOptional();
const contextMenu = useBlockContextMenuOptional();

const isDocument = computed(() => surface?.surface.value === 'document');

const previewUrl = computed(() => chrome?.previewUrl.value ?? host.preview.previewUrl(0));

const breakpoints = computed(() =>
    breakpointOptions(t, chrome?.breakpoints.value ?? { tablet: 768, mobile: 375 }),
);

const publishLabel = computed(() => {
    if (autosave?.publishing.value) {
        return t('publishing');
    }

    return autosave?.published.value ? t('published') : t('publish_changes');
});

const saveStatus = computed(() =>
    barSaveStatus(
        autosave
            ? {
                  status: autosave.status.value,
                  changed: autosave.changed.value,
                  publishing: autosave.publishing.value,
                  published: autosave.published.value,
              }
            : null,
        store.dirty.value,
    ),
);

const barStatus = computed<{ text: string; tone: BarStatusTone } | null>(() => {
    if (!isDocument.value && chrome) {
        if (chrome.state.value === 'error') {
            return { text: t('preview_error'), tone: 'danger' };
        }

        if (chrome.state.value === 'loading') {
            return { text: t('loading'), tone: 'muted' };
        }

        if (chrome.blockedNotice.value) {
            return { text: t('navigation_blocked'), tone: 'warning' };
        }
    }

    return saveStatus.value ? { text: t(saveStatus.value.key), tone: saveStatus.value.tone } : null;
});

/** Document surface has no iframe to refresh after publish/discard. */
const reloadAfter = computed(() => (isDocument.value ? null : chrome?.reload ?? null));

const publishMenuOpen = ref(false);

/** noop = nothing to ship: mutes the button instead of a dead-looking disable. */
const publishState = computed<'publishing' | 'success' | 'ready' | 'noop'>(() => {
    if (autosave?.publishing.value) {
        return 'publishing';
    }

    if (autosave?.published.value) {
        return 'success';
    }

    return autosave?.changed.value || store.dirty.value ? 'ready' : 'noop';
});

const publish = async (): Promise<void> => {
    if (autosave && publishState.value !== 'noop') {
        await publishDraft(autosave, reloadAfter.value);
    }
};

const draftChanged = computed(() => autosave?.changed.value ?? true);

/** One-time teachable moment: explain the working copy on first divergence. */
const draftHint = useOneTimeHint('draft-explained');

const draftNotice = computed(
    () =>
        autosave !== null &&
        draftHint.visible.value &&
        autosave.status.value !== 'loading' &&
        store.mode.value === 'edit' &&
        draftChanged.value,
);

const discardConfirm = useDiscardConfirmOptional();

const confirmDiscard = (): Promise<boolean> =>
    discardConfirm ? discardConfirm.request() : Promise.resolve(window.confirm(t('discard_confirm')));

const discardFromMenu = async (): Promise<void> => {
    if (!draftChanged.value) {
        return; // dimmed item — nothing to discard, keep the menu open
    }

    publishMenuOpen.value = false;

    if (autosave) {
        await discardDraft(autosave, confirmDiscard, reloadAfter.value);
    }
};

const takeover = async (): Promise<void> => {
    if (autosave) {
        await autosave.takeover();
        reloadAfter.value?.();
    }
};

const openPageMenu = (event: MouseEvent): void => {
    contextMenu?.openPage({ x: event.clientX, y: event.clientY });
};

const closePublishMenu = (): void => {
    publishMenuOpen.value = false;
};

onMounted(() => window.addEventListener('click', closePublishMenu));
onBeforeUnmount(() => window.removeEventListener('click', closePublishMenu));
</script>

<style scoped>
.pb2-bar {
    position: sticky;
    top: var(--pb2-dock-top, 0px);
    z-index: 20;
    background: var(--pb2-surface, #fafafa);
    border-bottom: 1px solid var(--pb2-border, #e4e4e7);
}

.pb2-bar__row {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    height: var(--pb2-bar-height, 40px);
    padding: 0 0.625rem;
    font-size: 0.6875rem;
    color: var(--pb2-text, #3f3f46);
}

.pb2-bar__zone {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    min-width: 0;
}

.pb2-bar__zone--left {
    flex: 1;
}

.pb2-bar__back {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.25rem 0.375rem;
    color: var(--pb2-muted, #a1a1aa);
    text-decoration: none;
    border-radius: 4px;
    transition: color 140ms ease;
    white-space: nowrap;
}

.pb2-bar__back:hover {
    color: var(--pb2-text, #3f3f46);
    text-decoration: none;
}

.pb2-bar__title {
    max-width: 18rem;
    padding: 0.25rem 0.375rem;
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--pb2-text, #3f3f46);
    background: none;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pb2-bar__title:hover {
    background: var(--pb2-surface-raised, #f0f0f2);
}

.pb2-bar__status {
    color: var(--pb2-muted, #a1a1aa);
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.pb2-bar__status--warning {
    color: var(--pb2-warning-accent, #f59e0b);
}

.pb2-bar__status--danger {
    color: var(--pb2-danger, #dc2626);
    animation: pb2-status-shake 320ms ease;
}

.pb2-bar__status--success {
    color: var(--pb2-success, #16a34a);
    animation: pb2-status-pop 280ms ease-out;
}

@keyframes pb2-status-shake {
    20%,
    60% {
        transform: translateX(-2px);
    }

    40%,
    80% {
        transform: translateX(2px);
    }
}

@keyframes pb2-status-pop {
    from {
        opacity: 0;
        transform: translateY(2px);
    }
}

@media (prefers-reduced-motion: reduce) {
    .pb2-bar__status--danger,
    .pb2-bar__status--success {
        animation: none;
    }
}

.pb2-bar__breakpoints {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    gap: 0.125rem;
    height: 100%;
}

.pb2-bar__breakpoint {
    position: relative;
    display: grid;
    place-items: center;
    width: 32px;
    height: 100%;
    color: var(--pb2-muted, #a1a1aa);
    background: none;
    border: 0;
    cursor: pointer;
    transition: color 140ms ease;
}

.pb2-bar__breakpoint:hover {
    color: var(--pb2-text, #3f3f46);
}

.pb2-bar__breakpoint--active {
    color: var(--pb2-text, #3f3f46);
}

.pb2-bar__breakpoint--active::after {
    content: '';
    position: absolute;
    left: 8px;
    right: 8px;
    bottom: 0;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background: var(--pb2-accent, #2563eb);
}

.pb2-bar__icon {
    display: grid;
    place-items: center;
    width: 28px;
    height: 28px;
    color: var(--pb2-muted, #a1a1aa);
    background: none;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    text-decoration: none;
    transition: color 140ms ease;
}

.pb2-bar__icon:hover {
    color: var(--pb2-text, #3f3f46);
    text-decoration: none;
}

.pb2-bar__icon:disabled {
    opacity: 0.4;
    cursor: default;
}

.pb2-bar__icon:active:not(:disabled),
.pb2-bar__breakpoint:active {
    transform: translateY(0.5px);
}

.pb2-bar__icon:focus-visible,
.pb2-bar__breakpoint:focus-visible,
.pb2-bar__back:focus-visible,
.pb2-bar__publish-main:focus-visible,
.pb2-bar__publish-caret:focus-visible {
    outline: 2px solid var(--pb2-accent, #2563eb);
    outline-offset: 1px;
}

.pb2-bar__sep {
    width: 1px;
    height: 16px;
    margin: 0 0.375rem;
    background: var(--pb2-border, #e4e4e7);
}

/* ============ publish split-button — the only accent action ============ */

.pb2-bar__publish {
    position: relative;
    display: flex;
    margin-left: 0.5rem;
    border-radius: 5px;
    overflow: visible;
}

.pb2-bar__publish-main {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    height: 26px;
    padding: 0 0.75rem;
    font-size: 0.6875rem;
    font-weight: 600;
    letter-spacing: 0.01em;
    color: var(--pb2-accent-contrast, #fff);
    background: var(--pb2-accent, #2563eb);
    border: 0;
    border-radius: 5px 0 0 5px;
    cursor: pointer;
}

.pb2-bar__publish-main--success {
    background: var(--pb2-success, #16a34a);
}

.pb2-bar__publish-spinner {
    width: 12px;
    height: 12px;
    border: 2px solid rgb(255 255 255 / 0.35);
    border-top-color: var(--pb2-accent-contrast, #fff);
    border-radius: 50%;
    animation: pb2-bar-spin 0.8s linear infinite;
}

@keyframes pb2-bar-spin {
    to {
        transform: rotate(360deg);
    }
}

.pb2-bar__publish-main:hover {
    filter: brightness(1.08);
}

.pb2-bar__publish-caret {
    display: grid;
    place-items: center;
    width: 22px;
    height: 26px;
    color: var(--pb2-accent-contrast, #fff);
    background: var(--pb2-accent, #2563eb);
    border: 0;
    border-left: 1px solid rgb(255 255 255 / 0.22);
    border-radius: 0 5px 5px 0;
    cursor: pointer;
}

.pb2-bar__publish-caret:hover {
    filter: brightness(1.08);
}

.pb2-bar__publish-main:disabled,
.pb2-bar__publish-caret:disabled {
    opacity: 0.5;
    cursor: default;
}

/* nothing to publish — muted, not disabled (tooltip + menu stay reachable) */
.pb2-bar__publish-main--muted,
.pb2-bar__publish-caret--muted {
    color: var(--pb2-muted, #a1a1aa);
    background: color-mix(in srgb, var(--pb2-border, #e4e4e7) 55%, transparent);
    cursor: default;
}

.pb2-bar__publish-caret--muted {
    border-left-color: color-mix(in srgb, var(--pb2-muted, #a1a1aa) 25%, transparent);
}

.pb2-bar__publish-main--muted:hover,
.pb2-bar__publish-caret--muted:hover {
    filter: none;
}

.pb2-bar__publish-menu {
    position: absolute;
    top: calc(100% + 4px);
    right: 0;
    z-index: 30;
    display: flex;
    flex-direction: column;
    min-width: 12rem;
    padding: 0.25rem;
    background: var(--pb2-surface-raised, #fff);
    border: 1px solid var(--pb2-border, #e4e4e7);
    border-radius: var(--pb2-radius, 6px);
    box-shadow: var(--pb2-panel-shadow, var(--pb2-shadow, 0 8px 28px rgb(0 0 0 / 0.2)));
}

.pb2-bar__publish-item {
    padding: 0.375rem 0.5rem;
    font-size: 0.6875rem;
    text-align: left;
    color: var(--pb2-danger, #dc2626);
    background: none;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
}

.pb2-bar__publish-item:hover {
    background: var(--pb2-surface, #fafafa);
}

.pb2-bar__publish-item--muted {
    color: var(--pb2-muted, #a1a1aa);
    cursor: default;
}

.pb2-bar__publish-item--muted:hover {
    background: none;
}

/* ============ lock / conflict — full-width strip under the bar ============ */

.pb2-bar__notice {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.375rem 0.625rem;
    font-size: 0.6875rem;
    color: var(--pb2-text, #3f3f46);
    background: var(--pb2-warning-bg, #fef3c7);
    border-bottom: 1px solid var(--pb2-warning-accent, #f59e0b);
}

.pb2-bar__notice--danger {
    background: var(--pb2-danger-bg, #fee2e2);
    border-bottom-color: var(--pb2-danger, #dc2626);
}

.pb2-bar__notice--info {
    background: color-mix(in srgb, var(--pb2-accent, #2563eb) 8%, var(--pb2-surface, #fafafa));
    border-bottom-color: color-mix(in srgb, var(--pb2-accent, #2563eb) 35%, transparent);
}

.pb2-bar__notice-action {
    padding: 0;
    font-size: 0.6875rem;
    font-weight: 600;
    color: var(--pb2-text, #3f3f46);
    background: none;
    border: 0;
    cursor: pointer;
    text-decoration: underline;
}
</style>
